import type React from "react";
import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Typography,
  alpha,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import type { RootState } from "../store";
import { TestId } from "../constants/testIds";
import DetailHeaderSplitActions, {
  type DetailHeaderAction,
} from "../components/common/DetailHeaderSplitActions";
import DetailPageHeader from "../components/common/DetailPageHeader";
import FinancialSummaryCards from "../components/common/FinancialSummaryCards";
import StatusWorkflowStepper from "../components/common/StatusWorkflowStepper";
import OrderHistorySection from "../components/orders/OrderHistorySection";
import SubmitRevisionDialog from "../components/orders/SubmitRevisionDialog";
import StandbyOrderContextSummaryCards from "../components/standbyOrders/StandbyOrderContextSummaryCards";
import ExpenseChecklist from "../components/uangJalanReports/ExpenseChecklist";
import {
  deleteStandbyOrderApi,
  getStandbyOrderByIdApi,
  readyForReviewStandbyOrderApi,
  reopenStandbyOrderApi,
  submitRevisionStandbyOrderApi,
} from "../api/standbyOrdersApi";
import {
  buildStandbyExpenseChecklistItems,
  getStandbyReadyForReviewMissingFieldKeys,
  isStandbyReadyForReview,
  type StandbyOrder,
} from "../types/standbyOrder";
import { isOrderApproved, isOrderEditable, OrderStatus } from "../types/order";
import { canMutateOrders } from "../types/user";
import { getErrorMessage } from "../utils/errorHandling";
import { getActiveRevisionNote } from "../utils/orderRevisionNotes";
import { getOrderWorkflowStepKey, ORDER_WORKFLOW_STEP_KEYS } from "../utils/orderStatusProgress";
import { toastEmitter } from "../utils/toastEmitter";
import type translations from "../translations/translations.json";

type TranslationKeys = keyof typeof translations.en;

const StandbyOrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.user);
  const [order, setOrder] = useState<StandbyOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [submitRevisionDialogOpen, setSubmitRevisionDialogOpen] = useState(false);

  const loadOrder = useCallback(async () => {
    if (!id) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setOrder(await getStandbyOrderByIdApi(id));
    } catch (err: any) {
      setError(getErrorMessage(err, t, "fetchStandbyOrder"));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  const runAction = async (
    action: () => Promise<StandbyOrder | undefined>,
    successMessage: TranslationKeys,
    context: string,
    onSuccess?: () => void,
  ) => {
    setActionLoading(true);
    setError(null);
    try {
      const result = await action();
      if (result) {
        setOrder(result);
      }
      toastEmitter.emit(t(successMessage), "success");
      onSuccess?.();
    } catch (err: any) {
      const errorMessage = getErrorMessage(err, t, context);
      setError(errorMessage);
      toastEmitter.emit(errorMessage, "error");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!order) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography color="error">{error || t("standby.notFound" as TranslationKeys)}</Typography>
      </Paper>
    );
  }

  const userCanMutate = !!user && canMutateOrders(user);
  const canEdit = userCanMutate && isOrderEditable(order.status);
  const canReopen = userCanMutate && order.status === OrderStatus.READY_FOR_REVIEW;
  const canDelete = userCanMutate && order.status === OrderStatus.OPEN;
  const canSubmitRevision = userCanMutate && order.status === OrderStatus.REVISION_REQUIRED;
  const lockMissingFieldKeys = getStandbyReadyForReviewMissingFieldKeys(order);
  const canLockForReview = isStandbyReadyForReview(order);
  const submitReviewDisabled =
    actionLoading || !userCanMutate || order.status !== OrderStatus.OPEN || !canLockForReview;

  const lockForReviewTooltip =
    lockMissingFieldKeys.length > 0 ? (
      <Box component="span" sx={{ display: "block" }}>
        {lockMissingFieldKeys.map((validationKey) => (
          <Typography
            key={validationKey}
            variant="caption"
            component="span"
            sx={{ display: "block" }}
          >
            • {t(validationKey as TranslationKeys)}
          </Typography>
        ))}
      </Box>
    ) : (
      ""
    );

  let submitReviewDisabledHint: React.ReactNode = "";
  if (!userCanMutate) {
    submitReviewDisabledHint = t("permission.operationsOnly" as TranslationKeys);
  } else if (order.status !== OrderStatus.OPEN) {
    submitReviewDisabledHint = t("orders.submitReviewDisabledStatus" as TranslationKeys);
  } else if (!canLockForReview) {
    submitReviewDisabledHint = lockForReviewTooltip;
  }

  const editAction: DetailHeaderAction = {
    id: "edit",
    label: t("Edit" as TranslationKeys),
    icon: <EditOutlinedIcon fontSize="small" />,
    onClick: () => navigate(`/orders/standby/${order.readableId}/edit`),
    disabled: actionLoading || !canEdit,
    disabledHint: !userCanMutate
      ? t("permission.operationsOnly" as TranslationKeys)
      : !isOrderEditable(order.status)
        ? t("orders.actionDisabledNotEditable" as TranslationKeys)
        : "",
    testId: TestId.STANDBY.EDIT_BUTTON,
  };

  const lockAction: DetailHeaderAction = {
    id: "lock",
    label: t("orders.submitForReview" as TranslationKeys),
    icon: <LockOutlinedIcon fontSize="small" />,
    onClick: () =>
      runAction(
        () => readyForReviewStandbyOrderApi(order._id),
        "orders.submittedForReviewSuccessfully",
        "readyForReview",
      ),
    disabled: submitReviewDisabled,
    disabledHint: submitReviewDisabledHint,
    testId: TestId.STANDBY.SUBMIT_REVIEW,
  };

  const reopenAction: DetailHeaderAction = {
    id: "reopen",
    label: t("orders.reopenForEditing" as TranslationKeys),
    icon: <ReplayOutlinedIcon fontSize="small" />,
    onClick: () =>
      runAction(
        () => reopenStandbyOrderApi(order._id),
        "orders.reopenedSuccessfully",
        "reopenStandbyOrder",
      ),
    disabled: actionLoading || !canReopen,
    disabledHint: !userCanMutate
      ? t("permission.operationsOnly" as TranslationKeys)
      : order.status !== OrderStatus.READY_FOR_REVIEW
        ? t("orders.reopenDisabledStatus" as TranslationKeys)
        : "",
    testId: TestId.STANDBY.REOPEN,
  };

  const submitRevisionAction: DetailHeaderAction = {
    id: "submit-revision",
    label: t("orders.submitRevision" as TranslationKeys),
    icon: <SendOutlinedIcon fontSize="small" />,
    onClick: () => setSubmitRevisionDialogOpen(true),
    disabled: actionLoading || !canSubmitRevision,
    disabledHint: !userCanMutate
      ? t("permission.operationsOnly" as TranslationKeys)
      : order.status !== OrderStatus.REVISION_REQUIRED
        ? t("orders.submitRevisionDisabledStatus" as TranslationKeys)
        : "",
    testId: TestId.STANDBY.SUBMIT_REVISION,
  };

  const deleteAction: DetailHeaderAction = {
    id: "delete",
    label: t("Delete" as TranslationKeys),
    icon: <DeleteOutlineIcon fontSize="small" />,
    onClick: () => setDeleteDialogOpen(true),
    disabled: actionLoading || !canDelete,
    disabledHint: !userCanMutate
      ? t("permission.operationsOnly" as TranslationKeys)
      : order.status !== OrderStatus.OPEN
        ? t("orders.deleteDisabledStatus" as TranslationKeys)
        : "",
    testId: TestId.STANDBY.DELETE,
  };

  const transitionActions = [submitRevisionAction, lockAction, reopenAction];
  const enabledTransition = transitionActions.find((action) => !action.disabled);
  const primary =
    enabledTransition ??
    (order.status === OrderStatus.REVISION_REQUIRED
      ? submitRevisionAction
      : order.status === OrderStatus.READY_FOR_REVIEW
        ? reopenAction
        : lockAction);

  const checklistItems = buildStandbyExpenseChecklistItems(
    order,
    t("standby.expense" as TranslationKeys),
  );
  const activeRevisionNote = getActiveRevisionNote(order);
  const workflowSteps = ORDER_WORKFLOW_STEP_KEYS.map((key) => ({
    key,
    label: t(key as TranslationKeys),
  }));

  return (
    <Box data-testid={TestId.STANDBY.DETAIL} sx={{ pb: 4 }}>
      <DetailPageHeader
        compact
        title={t("standby.detailTitle" as TranslationKeys)}
        subtitle={
          <Box component="span" data-testid={TestId.STANDBY.READABLE_ID}>
            {order.readableId}
          </Box>
        }
        statusLabel={t("Status" as TranslationKeys)}
        status={order.status}
        statusTestId={TestId.STANDBY.STATUS}
        metaLine={
          <>
            {order.date ? `${t("standby.date" as TranslationKeys)}: ${order.date}` : null}
            {order.teamOperations?.name
              ? `${order.date ? " · " : ""}${order.teamOperations.name}`
              : null}
            {order.location?.name ? ` · ${order.location.name}` : null}
          </>
        }
        progressSlot={
          <StatusWorkflowStepper
            compact
            steps={workflowSteps}
            activeStepKey={getOrderWorkflowStepKey(order.status)}
          />
        }
        statusNote={
          order.status === OrderStatus.REVISION_REQUIRED && activeRevisionNote ? (
            <Paper
              variant="outlined"
              sx={{
                px: 1.5,
                py: 0.75,
                borderRadius: 1,
                bgcolor: (theme) => alpha(theme.palette.warning.main, 0.1),
                borderColor: "warning.main",
              }}
              data-testid={TestId.STANDBY.REVISION_NOTES}
            >
              <Typography
                variant="caption"
                color="warning.dark"
                display="block"
                sx={{ fontWeight: 700 }}
              >
                {t("orders.revisionNote" as TranslationKeys)}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {activeRevisionNote}
              </Typography>
            </Paper>
          ) : undefined
        }
        actions={
          <DetailHeaderSplitActions
            leadingActions={canEdit ? [editAction] : []}
            primary={primary}
            menuActions={[
              ...(canEdit ? [] : [editAction]),
              ...transitionActions.filter((action) => action.id !== primary.id),
              deleteAction,
            ]}
            menuTestId={TestId.STANDBY.HEADER_ACTIONS_MENU}
            finalState={isOrderApproved(order.status)}
          />
        }
      />

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <StandbyOrderContextSummaryCards order={order} />

      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          alignItems: "start",
        }}
      >
        <Box
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          data-testid={TestId.STANDBY.EXPENSE_CHECKLIST}
        >
          <ExpenseChecklist
            orderId={order._id}
            items={checklistItems}
            disabled
            disabledHint={t("orders.checklistReadOnlyHint" as TranslationKeys)}
          />
          <FinancialSummaryCards
            totalPaid={order.totalPaid}
            totalVerified={order.totalApprovedCost}
            difference={order.difference}
            totalPaidLabel={t("reports.orderTotalPaid" as TranslationKeys)}
            totalVerifiedLabel={t("reports.totalApproved" as TranslationKeys)}
            differenceLabel={t("reports.difference" as TranslationKeys)}
            totalPaidTestId={TestId.STANDBY.TOTAL_PAID}
            totalVerifiedTestId={TestId.STANDBY.TOTAL_APPROVED}
            differenceTestId={TestId.STANDBY.DIFFERENCE}
            totalVerifiedDisplay={
              isOrderApproved(order.status) ? undefined : t("reports.pending" as TranslationKeys)
            }
            differenceDisplay={
              isOrderApproved(order.status) ? undefined : t("reports.pending" as TranslationKeys)
            }
            orientation="horizontal"
            sx={{ mb: 0 }}
          />
        </Box>
        <OrderHistorySection
          histories={order.histories ?? []}
          revisionNote={order.revisionNote}
          sx={{ mt: 0, height: "100%" }}
        />
      </Box>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t("Delete" as TranslationKeys)}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("standby.deleteConfirm" as TranslationKeys)}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            {t("Cancel" as TranslationKeys)}
          </Button>
          <Button
            color="error"
            variant="contained"
            data-testid={TestId.COMMON.CONFIRM_BUTTON}
            onClick={() => {
              setDeleteDialogOpen(false);
              runAction(
                async () => {
                  await deleteStandbyOrderApi(order._id);
                  return undefined;
                },
                "standby.deletedSuccessfully",
                "deleteStandbyOrder",
                () => navigate("/orders"),
              );
            }}
          >
            {t("Delete" as TranslationKeys)}
          </Button>
        </DialogActions>
      </Dialog>

      <SubmitRevisionDialog
        open={submitRevisionDialogOpen}
        loading={actionLoading}
        onClose={() => setSubmitRevisionDialogOpen(false)}
        onSubmit={async (note) => {
          await runAction(
            () => submitRevisionStandbyOrderApi(order._id, note),
            "orders.revisionSubmittedSuccessfully",
            "submitRevision",
            () => setSubmitRevisionDialogOpen(false),
          );
        }}
      />
    </Box>
  );
};

export default StandbyOrderDetailPage;
